import React from 'react';
import PropTypes from 'prop-types';
import {
  Cushion,
  FlexContainer,
  LinearGradient,
  Rectangle,
  Space,
  Typography,
} from '@pitchero/react-ui';
import { Trans } from '../../../lib/i18n';
import ShareButton from '../../social/share-button';

const FundraisingTotal = ({
  fundraisingCount,
  fundraisingPercent,
  fundraisingTarget,
  fundraisingTotal,
  shareProps,
}) => {
  if (!fundraisingTarget) {
    return (
      <Cushion vertical="large" horizontal="medium" style={{ textAlign: 'center' }}>
        {fundraisingCount > 0 && (
          <Space bottom="medium">
            <Typography preset="title--large" component="div" align="center" color="primary">
              {fundraisingTotal}
            </Typography>
            <Typography preset="subtitle--small" component="div" align="center">
              <Trans i18nKey="shop:fundraising_raised_no_target" count={fundraisingCount}>
                {`Raised from ${fundraisingCount} donations`}
              </Trans>
            </Typography>
          </Space>
        )}
        <ShareButton {...shareProps} withText />
      </Cushion>
    );
  }

  const fillPercent = Math.min(100, fundraisingPercent);
  let precentFontSize = 42;
  if (fundraisingPercent > 1000) {
    precentFontSize = 26;
  } else if (fundraisingPercent > 100) {
    precentFontSize = 32;
  }

  return (
    <FlexContainer
      alignItems="center"
      responsive={[
        {
          maxWidth: 'tab',
          props: {
            flexDirection: 'column',
          },
        },
      ]}
    >
      <Cushion
        vertical="medium"
        right="medium"
        component="div"
        responsive={[
          {
            maxWidth: 'tab',
            props: {
              right: '0',
            },
          },
        ]}
      >
        <Rectangle border="3px solid #085a7f" fill="#00a0d1" radius="circle">
          <Cushion all={3} component="div">
            <LinearGradient
              component="div"
              angle={0}
              style={{
                height: 110,
                width: 110,
                borderRadius: 55,
                whiteSpace: 'nowrap',
              }}
              stops={[
                ['0%', '#085a7f'],
                [`${fillPercent}%`, '#085a7f'],
                [`${fillPercent}%`, '#00a0d1'],
              ]}
            >
              <Typography
                family="'Montserrat', 'Gibson', 'Roboto', Arial, Helvetica, sans-serif"
                weight="bold"
                size={precentFontSize}
                lineHeight="110px"
                color="white"
                align="center"
                component="div"
              >
                {`${fundraisingPercent}%`}
              </Typography>
            </LinearGradient>
          </Cushion>
        </Rectangle>
      </Cushion>
      <Typography
        align="left"
        responsive={[
          {
            maxWidth: 'tab',
            props: {
              align: 'center',
            },
          },
        ]}
        style={{ flex: 1 }}
      >
        <Typography preset="title--large" component="div" color="#00a0d1">
          {fundraisingTotal}
        </Typography>
        <Space vertical="small">
          <Typography preset="subtitle--small" component="div">
            <Trans
              i18nKey="shop:fundraising_raised_with_target"
              values={{ target: fundraisingTarget }}
            >
              {`Raised of ${fundraisingTarget} target`}
            </Trans>
          </Typography>
          {fundraisingCount > 0 && (
            <Typography preset="subtitle--small" component="div">
              <Trans i18nKey="shop:fundraising_raised_donations" count={fundraisingCount}>
                {`from ${fundraisingCount} donations`}
              </Trans>
            </Typography>
          )}
        </Space>
        <ShareButton {...shareProps} withText />
      </Typography>
    </FlexContainer>
  );
};

FundraisingTotal.defaultProps = {
  fundraisingPercent: 0,
  fundraisingTarget: null,
};

FundraisingTotal.propTypes = {
  fundraisingCount: PropTypes.number.isRequired,
  fundraisingPercent: PropTypes.number,
  fundraisingTarget: PropTypes.string,
  fundraisingTotal: PropTypes.string.isRequired,
  shareProps: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
};

export default FundraisingTotal;
